import { useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Empty,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Steps,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LEADS,
  DEFAULT_REDUCERS,
  DEFAULT_SYNC_DEC_RATIOS,
  selectMotor,
  type Recommendation,
  type SelectOptions,
} from '@noumase/cylinder-calc';
import { useMotorLibrary } from './motorStore';
import { RecommendationCard } from './RecommendationCard';
import { StepCard, type StepState } from './StepCard';

const { Text } = Typography;

interface FormValues {
  F: number;
  S: number;
  loadSafetyFactor: number;
  speedSafetyFactor: number;
  topN: number;
  leads: string[];
  reducers: string[];
  syncDecRatios: string[];
}

const DEFAULT_FORM: FormValues = {
  F: 640,
  S: 208.33,
  loadSafetyFactor: 1.2,
  speedSafetyFactor: 1.0,
  topN: 5,
  leads: DEFAULT_LEADS.map(String),
  reducers: DEFAULT_REDUCERS.map(String),
  syncDecRatios: DEFAULT_SYNC_DEC_RATIOS.map(String),
};

function parseNumberArray(arr: string[] | undefined): number[] {
  return (arr ?? [])
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function computeMatchPercents(recs: Recommendation[]): number[] {
  if (recs.length === 0) return [];
  const scores = recs.map((r) => r.score);
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  if (max === min) return recs.map(() => 100);
  return scores.map((s) => 100 * (1 - (s - min) / (max - min)));
}

export default function ReversePanel() {
  const { t } = useTranslation();
  const { motors } = useMotorLibrary();
  const [form] = Form.useForm<FormValues>();
  const [recs, setRecs] = useState<Recommendation[] | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 实时监听工况输入：F / S 都 > 0 才允许进入候选集步骤
  const watchedF = Form.useWatch('F', form);
  const watchedS = Form.useWatch('S', form);
  const dutyReady = !!watchedF && watchedF > 0 && !!watchedS && watchedS > 0;
  const hasResult = recs !== null;

  const dutyState: StepState = hasResult ? 'completed' : 'active';
  const candidatesState: StepState = !dutyReady
    ? 'inactive'
    : hasResult
      ? 'completed'
      : 'active';
  const resultState: StepState = !hasResult ? 'inactive' : 'active';

  // antd 顶部 Steps current（0/1/2）
  const topCurrent = hasResult ? 2 : dutyReady ? 1 : 0;

  const runCalc = () => {
    const values = form.getFieldsValue(true);
    setCalculating(true);
    setError(null);
    try {
      const opts: SelectOptions = {
        loadSafetyFactor: values.loadSafetyFactor,
        speedSafetyFactor: values.speedSafetyFactor,
        topN: values.topN,
        motors,
      };
      const leads = parseNumberArray(values.leads);
      if (leads.length) opts.leads = leads;
      const reducers = parseNumberArray(values.reducers);
      if (reducers.length) opts.reducers = reducers;
      const syncDec = parseNumberArray(values.syncDecRatios);
      if (syncDec.length) opts.syncDecRatios = syncDec;
      const result = selectMotor({ F: values.F, S: values.S }, opts);
      setRecs(result);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setRecs([]);
    } finally {
      setCalculating(false);
    }
  };

  const handleReset = () => {
    form.setFieldsValue(DEFAULT_FORM);
    setRecs(null);
    setError(null);
  };

  const matchPercents = recs ? computeMatchPercents(recs) : [];

  const clearAll = (
    <Button
      type="link"
      size="small"
      onClick={handleReset}
      style={{ padding: 0 }}
    >
      ⊗ {t('reverse.steps.clearAll')}
    </Button>
  );

  return (
    <>
      <Steps
        current={topCurrent}
        size="small"
        items={[
          { title: t('reverse.steps.duty') },
          { title: t('reverse.steps.candidates') },
          { title: t('reverse.steps.result') },
        ]}
        style={{
          marginBottom: 16,
          padding: '12px 24px',
          background: '#f7f8fa',
          border: '1px solid #ebedf0',
          borderRadius: 4,
        }}
      />

      <Form form={form} layout="vertical" initialValues={DEFAULT_FORM} preserve>
        {/* 步 1：工况输入 */}
        <StepCard
          index={1}
          title={t('reverse.steps.duty')}
          state={dutyState}
          extra={dutyState === 'active' || dutyState === 'completed' ? clearAll : undefined}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t('reverse.fields.thrust')}
                name="F"
                rules={[
                  { required: true, message: t('common.required') },
                  { type: 'number', min: 0.01, message: t('common.gtZero') },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  step={10}
                  placeholder={t('reverse.thrustPh')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t('reverse.fields.speed')}
                name="S"
                rules={[
                  { required: true, message: t('common.required') },
                  { type: 'number', min: 0.01, message: t('common.gtZero') },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  step={10}
                  placeholder={t('reverse.speedPh')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label={t('reverse.fields.topN')} name="topN">
                <InputNumber style={{ width: '100%' }} step={1} min={1} max={20} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t('reverse.fields.loadSafetyFactor')}
                name="loadSafetyFactor"
                tooltip={t('reverse.fields.loadSafetyFactorTip')}
                rules={[{ type: 'number', min: 1, message: t('common.gteOne') }]}
              >
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t('reverse.fields.speedSafetyFactor')}
                name="speedSafetyFactor"
                tooltip={t('reverse.fields.speedSafetyFactorTip')}
                rules={[{ type: 'number', min: 1, message: t('common.gteOne') }]}
              >
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
          </Row>
        </StepCard>

        {/* 步 2：候选集（可选） */}
        <StepCard
          index={2}
          title={t('reverse.steps.candidates')}
          state={candidatesState}
          placeholder={t('reverse.steps.candidatesPlaceholder')}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('reverse.steps.candidatesHint')}
          </Text>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label={t('reverse.advanced.leads')}
                name="leads"
                tooltip={t('reverse.advanced.leadsTip')}
              >
                <Select
                  mode="tags"
                  placeholder={t('reverse.advanced.leadsPh')}
                  tokenSeparators={[',', ' ']}
                  options={DEFAULT_LEADS.map((v) => ({
                    value: String(v),
                    label: `${v} mm`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={t('reverse.advanced.reducers')}
                name="reducers"
                tooltip={t('reverse.advanced.reducersTip')}
              >
                <Select
                  mode="tags"
                  placeholder={t('reverse.advanced.reducersPh')}
                  tokenSeparators={[',', ' ']}
                  options={DEFAULT_REDUCERS.map((v) => ({
                    value: String(v),
                    label: String(v),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={t('reverse.advanced.syncDec')}
                name="syncDecRatios"
                tooltip={t('reverse.advanced.syncDecTip')}
              >
                <Select
                  mode="tags"
                  placeholder={t('reverse.advanced.syncDecPh')}
                  tokenSeparators={[',', ' ']}
                  options={DEFAULT_SYNC_DEC_RATIOS.map((v) => ({
                    value: String(v),
                    label: String(v),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleReset}>{t('common.reset')}</Button>
              <Button type="primary" loading={calculating} onClick={runCalc}>
                {hasResult ? t('reverse.steps.recalc') : t('reverse.steps.calc')}
              </Button>
            </Space>
          </div>
        </StepCard>

        {/* 步 3：选型结果 */}
        <StepCard
          index={3}
          title={t('reverse.steps.result')}
          state={resultState}
          placeholder={t('reverse.steps.resultPlaceholder')}
        >
          {error && (
            <Alert
              type="error"
              message={t('common.calcError')}
              description={error}
              style={{ marginBottom: 12 }}
              closable
              onClose={() => setError(null)}
            />
          )}
          {recs === null ? (
            <Empty description={t('reverse.empty')} />
          ) : recs.length === 0 ? (
            <Alert
              type="warning"
              message={t('reverse.noMatchTitle')}
              description={t('reverse.noMatchDesc')}
            />
          ) : (
            recs.map((r, idx) => (
              <RecommendationCard
                key={`${r.motor.id}-${r.lead}-${r.ratio.reducer}-${r.ratio.syncDec}-${r.ratio.syncInc}`}
                rec={r}
                rank={idx + 1}
                matchPercent={matchPercents[idx] ?? 0}
                rawScore={r.score}
              />
            ))
          )}
        </StepCard>
      </Form>
    </>
  );
}
